import { BusinessObjectBase } from './base';
import type { Candidate } from './candidate';
import type { Position } from './position';

export interface PlacementData {
  readonly id: number;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly candidateId: number;
  readonly candidate?: Candidate;
  readonly positionId: number;
  readonly position?: Position;
}

export interface Placement extends Readonly<PlacementData> {}
export class Placement extends BusinessObjectBase {
  static readonly entityName = 'Placement' as const;
  static readonly plural = 'placements';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Placement[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Placement(data));
  }

  static async findOne(filter?: any): Promise<Placement | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Placement(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<Placement> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Placement(data);
  }

  static async create(data: Omit<PlacementData, 'id'>): Promise<Placement> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Placement(result);
  }

  static async updateById(
    id: number,
    data: Partial<PlacementData>,
  ): Promise<Placement> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new Placement(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
